import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import SearchSelect from "@/components/common/SearchSelect";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { fmtINR } from "@/lib/format";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, Trash2, IdCard, MapPin, Scale, Briefcase, Paperclip, Download, PenSquare } from "lucide-react";
import OnboardingWizard from "@/components/OnboardingWizard";
import { GstinQuickFetch, applyGstinLookup } from "@/components/GstinQuickFetch";

const MSME_LABELS = {
  not_registered: { label: "—", color: "text-muted-foreground" },
  micro: { label: "MSME · Micro", color: "text-emerald-600" },
  small: { label: "MSME · Small", color: "text-blue-600" },
  medium: { label: "MSME · Medium", color: "text-purple-600" },
};

const STATES = [
  ["27", "Maharashtra"], ["29", "Karnataka"], ["24", "Gujarat"], ["07", "Delhi"],
  ["33", "Tamil Nadu"], ["09", "Uttar Pradesh"], ["19", "West Bengal"], ["36", "Telangana"],
  ["06", "Haryana"], ["03", "Punjab"], ["32", "Kerala"], ["23", "Madhya Pradesh"],
  ["08", "Rajasthan"], ["10", "Bihar"], ["20", "Jharkhand"], ["21", "Odisha"],
  ["22", "Chhattisgarh"], ["37", "Andhra Pradesh"], ["04", "Chandigarh"], ["05", "Uttarakhand"],
  ["30", "Goa"], ["02", "Himachal Pradesh"], ["01", "Jammu & Kashmir"],
];
const STATE_OPTS = STATES.map(([code, name]) => ({ value: code, label: name, sublabel: `Code ${code}` }));

const CUSTOMER_TYPES = [
  { value: "revenue_ops",     label: "Revenue — from Operations",   sublabel: "Sch III · Revenue from Operations" },
  { value: "revenue_non_ops", label: "Revenue — Other / Non-Ops",   sublabel: "Sch III · Other Income" },
  { value: "service",         label: "Service Revenue",             sublabel: "Services under SAC" },
  { value: "export",          label: "Export / Overseas customer",  sublabel: "Zero-rated / LUT" },
];

// Iter-220 · Vendor expense classification — mirror of CUSTOMER_TYPES for
// the vendor onboarding flow. Vendors don't generate revenue; they feed
// Sch III P&L buckets on the expense side.
const VENDOR_TYPES = [
  { value: "cost_of_materials",       label: "Cost of Materials consumed", sublabel: "Sch III · raw materials / stock-in-trade" },
  { value: "employee_benefit",        label: "Employee / HR vendor",        sublabel: "Sch III · Employee benefit expense" },
  { value: "finance_cost",            label: "Finance / Interest vendor",   sublabel: "Sch III · Finance costs" },
  { value: "other_expense",           label: "Other operating expense",     sublabel: "Sch III · Other expenses (default)" },
  { value: "capex",                   label: "Capital / Fixed asset vendor",sublabel: "Capitalised — not P&L" },
  { value: "overseas",                label: "Import / Overseas vendor",    sublabel: "Reverse charge · Customs" },
];

const empty = (type) => ({
  party_type: type,
  name: "", legal_name: "", trade_name: "", display_name: "",
  // Iter-220 · `customer_code` is the DB column name but the user-visible
  // label flips to "Vendor code" when party_type=vendor. Default type
  // code mirrors expense-side default for vendors.
  customer_code: "", customer_type: type === "vendor" ? "other_expense" : "revenue_ops",
  email: "", phone: "",
  gstin: "", gst_status: "registered", pan: "",
  state: "", state_code: "",
  address_line1: "", address_line2: "", city: "", pincode: "",
  shipping_address: "", shipping_state_code: "",
  sales_person_name: "", credit_terms_days: 30,
  msme_status: "not_registered", udyam_number: "",
  default_tds_section: "", default_tds_rate: 0,
  tds_deduct_default: false, tds_visible_on_invoice: true,
  // TCS applies ONLY to sales (Section 206C) — never shown on vendor form.
  tcs_applicable: false, tcs_section: "206C(1H)", tcs_rate: 0.1, tcs_threshold_inr: 5000000,
});

export default function PartiesPage({ type = "customer" }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [viewParty, setViewParty] = useState(null);
  const [partyLedger, setPartyLedger] = useState(null);
  const [form, setForm] = useState(empty(type));
  const [gstinFetching, setGstinFetching] = useState(false);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("id");

  const load = async () => setRows((await api.get("/parties", { params: { party_type: type, ...(q ? { q } : {}) } })).data);
  useEffect(() => { load(); setForm(empty(type)); /* eslint-disable-next-line */ }, [type, q]);

  const openParty = async (p) => {
    setViewParty(p);
    setPartyLedger(null);
    try {
      const r = await api.get(`/parties/${p.id}/ledger`);
      setPartyLedger(r.data);
    } catch (e) { setPartyLedger({ entries: [], balance: p.balance || 0 }); }
  };

  const create = async () => {
    if (!form.name && !form.legal_name) return toast.error("Legal or trade name required");
    // Iter-220 · PAN mandatory for both — for customers it's TCS 206C
    // compliance, for vendors it's TDS 194C/194J/etc. compliance.
    if (!form.pan) return toast.error(type === "customer"
      ? "PAN is mandatory for TCS compliance (Section 206C)"
      : "PAN is mandatory for TDS compliance — Zenith cannot withhold TDS without it");
    // Iter-199 · GSTIN is optional — a party can be URD, personal, foreign,
    // or a walk-in retail customer.
    const payload = { ...form, name: form.name || form.trade_name || form.legal_name };
    try {
      await api.post("/parties", payload);
      toast.success(`${noun} created`); setOpen(false); setForm(empty(type)); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Iter-199 · Multi-select + bulk / wipe delete
  const [selected, setSelected] = useState(new Set());
  const clearSel = () => setSelected(new Set());
  const toggleOne = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((r) => r.id)));
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} ${type}(s)?\n\n• Linked ones → marked inactive (kept for audit)\n• Orphan ones → deleted permanently`)) return;
    try {
      const r = await api.post("/parties/bulk-delete", { party_ids: Array.from(selected) });
      toast.success(`Deleted ${r.data.deleted} · soft-deleted ${r.data.soft_deleted}`);
      clearSel(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
  };
  const wipeAll = async () => {
    const phrase = window.prompt(`⚠︎ Wipe ALL ${noun.toLowerCase()}s in this company?\n\nThis is IRREVERSIBLE for orphan ${noun.toLowerCase()}s.\nLinked ones will be soft-deleted (kept for audit).\n\nType   WIPE ALL DATA   to confirm:`);
    if (phrase !== "WIPE ALL DATA") { if (phrase !== null) toast.error("Confirmation phrase did not match — nothing wiped"); return; }
    try {
      const r = await api.post("/parties/wipe-all", { confirm: "WIPE ALL DATA" });
      toast.success(`Wiped — ${r.data.deleted} deleted · ${r.data.soft_deleted} soft-deleted`);
      clearSel(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Wipe failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}? If linked, will be marked inactive.`)) return;
    try { await api.delete(`/parties/${p.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const noun = type === "customer" ? "Customer" : "Vendor";
  const filtered = useMemo(() => rows.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase())), [rows, q]);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid={`${type}s-page`}>
      <PageHeader eyebrow="Masters" title={`${noun}s`}
        description={type === "customer"
          ? "Complete customer master — identification (PAN mandatory), address (bill-to / ship-to), GST, TCS setup, commercial terms."
          : "Vendor master — identification, GST, TDS defaults, MSME status, commercial terms."}
        actions={
          <div className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-48 rounded-sm h-9" data-testid={`${type}-search`} />
            <Button onClick={() => setWizardOpen(true)} variant="outline"
                    className="rounded-sm gap-1.5 h-9 border-accent/40 text-accent hover:bg-accent/10" data-testid={`${type}-wizard-btn`}>
              <Sparkles className="w-3.5 h-3.5" /> Wizard
            </Button>
            <Button onClick={() => { setForm(empty(type)); setTab("id"); setOpen(true); }} className="rounded-sm bg-accent text-white" data-testid={`new-${type}-btn`}>+ {noun}</Button>
          </div>
        } />

      <OnboardingWizard open={wizardOpen} onOpenChange={setWizardOpen}
                          partyType={type}
                          onCreated={() => load()} />

      <div className="card-sharp overflow-hidden">
        {selected.size > 0 && (
          <div className="p-3 border-b bg-accent/10 flex items-center justify-between flex-wrap gap-2" data-testid="party-bulk-bar">
            <span className="text-sm font-medium">{selected.size} selected</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={clearSel} data-testid="party-bulk-clear">Clear</Button>
              <Button size="sm" variant="outline" onClick={bulkDelete}
                       className="text-red-600 border-red-500/60 hover:bg-red-50" data-testid="party-bulk-delete">
                Delete Selected
              </Button>
            </div>
          </div>
        )}
        <div className="p-3 border-b bg-muted/30 flex items-center justify-end">
          <Button size="sm" variant="outline" onClick={wipeAll}
                    className="text-red-600 border-red-500/60 hover:bg-red-50" data-testid={`${type}-wipe-all`}>
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete ALL {noun}s
          </Button>
        </div>
        <table className="w-full dense">
          <thead><tr>
            <th className="w-9"><input type="checkbox"
                                          checked={filtered.length > 0 && selected.size === filtered.length}
                                          onChange={toggleAll}
                                          data-testid="party-select-all" /></th>
            <th>Code</th><th>Name</th><th>GSTIN / PAN</th><th>State</th><th>Type</th><th>MSME</th><th className="text-right">Balance</th><th></th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-muted-foreground">No {noun.toLowerCase()}s yet</td></tr>}
            {filtered.map((p) => (
              <tr key={p.id} onClick={() => openParty(p)} className="cursor-pointer hover:bg-muted/40" data-testid={`party-row-${p.name.replace(/\s+/g, "_")}`}>
                <td onClick={(e) => e.stopPropagation()} className="w-9">
                  <input type="checkbox"
                         checked={selected.has(p.id)}
                         onChange={() => toggleOne(p.id)}
                         data-testid={`party-sel-${p.name.replace(/\s+/g, "_")}`} />
                </td>
                <td className="font-mono text-xs">{p.customer_code || "—"}</td>
                <td>
                  <div>{p.display_name || p.name}</div>
                  {p.legal_name && p.legal_name !== p.name && <div className="text-[10px] text-muted-foreground">Legal: {p.legal_name}</div>}
                </td>
                <td className="text-xs font-mono">
                  {p.gstin && <div>{p.gstin}</div>}
                  {p.pan && <div className="text-muted-foreground">{p.pan}</div>}
                  {!p.gstin && !p.pan && <span className="text-muted-foreground">—</span>}
                </td>
                <td className="text-xs">{p.state || "—"} {p.state_code ? `(${p.state_code})` : ""}</td>
                <td className="text-xs text-muted-foreground">{(p.customer_type || "").replaceAll("_", " ") || "—"}</td>
                <td className={`text-xs ${MSME_LABELS[p.msme_status]?.color || ""}`}>{MSME_LABELS[p.msme_status]?.label || p.msme_status}</td>
                <td className="text-right tabular-nums">{fmtINR(p.balance)}</td>
                <td className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"
                    onClick={() => { setForm({ ...empty(type), ...p, addresses: p.addresses || [] }); setTab("id"); setOpen(true); }}
                    data-testid={`edit-party-${p.name.replace(/\s+/g, "_")}`}>
                    <PenSquare className="w-3.5 h-3.5" /> Edit
                  </Button>
                  {type === "vendor" && (
                    <Button variant="ghost" size="sm" className="h-7 gap-1 text-[11px]"
                             data-testid={`tds-statement-${p.name.replace(/\s+/g, "_")}`}
                             title="Download TDS Statement PDF for this vendor's current FY"
                             onClick={() => {
                               const fy_from = new Date(new Date().getMonth() < 3 ? new Date().getFullYear() - 1 : new Date().getFullYear(), 3, 1).toISOString().slice(0, 10);
                               const fy_to   = new Date(new Date().getMonth() < 3 ? new Date().getFullYear() : new Date().getFullYear() + 1, 2, 31).toISOString().slice(0, 10);
                               const url = `${process.env.REACT_APP_BACKEND_URL}/api/tds/vendor-statement/${p.id}.pdf?from_date=${fy_from}&to_date=${fy_to}`;
                               window.open(url, "_blank");
                             }}>
                      <Download className="w-3.5 h-3.5" /> TDS
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-7" onClick={() => remove(p)} data-testid={`del-party-${p.name.replace(/\s+/g, "_")}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={!!viewParty} onOpenChange={(o) => !o && setViewParty(null)}>
        <SheetContent side="right" className="w-full sm:max-w-3xl rounded-none overflow-y-auto space-y-4" data-testid="party-view-drawer">
          <SheetHeader className="pb-3 border-b">
            <SheetTitle className="font-heading flex items-center gap-3">
              <span className="text-[10px] font-mono uppercase text-accent px-2 py-0.5 rounded-sm bg-accent/10">{viewParty?.party_type}</span>
              {viewParty?.display_name || viewParty?.name}
            </SheetTitle>
          </SheetHeader>
          {viewParty && (
            <div className="text-sm space-y-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 border rounded-sm p-4 bg-muted/20">
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Code</div><div className="mt-1 font-mono">{viewParty.customer_code || "—"}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Legal Name</div><div className="mt-1">{viewParty.legal_name || viewParty.name}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">GSTIN</div><div className="mt-1 font-mono">{viewParty.gstin || "—"}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">PAN</div><div className="mt-1 font-mono">{viewParty.pan || "—"}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">State</div><div className="mt-1">{viewParty.state} {viewParty.state_code ? `(${viewParty.state_code})` : ""}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">GST Status</div><div className="mt-1 font-mono uppercase text-xs">{viewParty.gst_status || "—"}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Email</div><div className="mt-1 text-xs">{viewParty.email || "—"}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Phone</div><div className="mt-1 font-mono">{viewParty.phone || "—"}</div></div>
                <div className="col-span-2"><div className="text-[10px] font-mono uppercase text-muted-foreground">Address</div><div className="mt-1 text-xs">{[viewParty.address_line1, viewParty.address_line2, viewParty.city, viewParty.pincode].filter(Boolean).join(", ") || "—"}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Credit terms</div><div className="mt-1">{viewParty.credit_terms_days || 0} days</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Credit limit</div><div className="mt-1 tabular-nums">{fmtINR(viewParty.credit_limit || 0)}</div></div>
                <div className="col-span-2 border-t pt-3">
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">Outstanding balance</div>
                  <div className={`mt-1 text-2xl font-bold tabular-nums ${(viewParty.balance || 0) < 0 ? "text-red-600" : "text-emerald-600"}`}>{fmtINR(Math.abs(viewParty.balance || 0))}<span className="text-xs ml-2 text-muted-foreground">{(viewParty.balance || 0) < 0 ? "we owe them" : (viewParty.balance || 0) > 0 ? "they owe us" : "settled"}</span></div>
                </div>
              </div>

              <div className="border rounded-sm overflow-hidden">
                <div className="p-3 border-b text-[10px] font-mono uppercase text-muted-foreground bg-muted/40 flex items-center justify-between">
                  <span>// Ledger (last {partyLedger?.entries?.length || 0} entries)</span>
                  {partyLedger && <span className="text-accent">Bal: {fmtINR(partyLedger.balance || 0)}</span>}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {!partyLedger && <div className="p-8 text-center text-muted-foreground text-sm">Loading ledger…</div>}
                  {partyLedger && (!partyLedger.entries || partyLedger.entries.length === 0) && <div className="p-8 text-center text-muted-foreground text-sm">No transactions yet</div>}
                  {partyLedger?.entries?.length > 0 && (
                    <table className="w-full text-xs">
                      <thead className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/20">
                        <tr>
                          <th className="p-2 text-left">Date</th>
                          <th className="p-2 text-left">Voucher</th>
                          <th className="p-2 text-left">Narration</th>
                          <th className="p-2 text-right">Debit</th>
                          <th className="p-2 text-right">Credit</th>
                          <th className="p-2 text-right">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partyLedger.entries.map((e, i) => (
                          <tr key={i} className="border-t hover:bg-muted/20">
                            <td className="p-2 font-mono">{e.date}</td>
                            <td className="p-2 font-mono">{e.voucher_number}</td>
                            <td className="p-2 max-w-sm">{e.narration || "—"}</td>
                            <td className="p-2 text-right tabular-nums">{e.debit ? fmtINR(e.debit) : ""}</td>
                            <td className="p-2 text-right tabular-nums">{e.credit ? fmtINR(e.credit) : ""}</td>
                            <td className="p-2 text-right tabular-nums font-medium">{fmtINR(e.running_balance || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none overflow-y-auto">
          <SheetHeader className="pb-3 border-b"><SheetTitle className="font-heading">New {noun}</SheetTitle></SheetHeader>
          <GstinQuickFetch form={form} setForm={setForm} />
          <Tabs value={tab} onValueChange={setTab} className="mt-4">
            <TabsList className="rounded-sm grid grid-cols-5 w-full">
              <TabsTrigger value="id" className="rounded-sm gap-1.5 text-xs" data-testid="tab-id"><IdCard className="w-3.5 h-3.5" /> Identification</TabsTrigger>
              <TabsTrigger value="addr" className="rounded-sm gap-1.5 text-xs" data-testid="tab-addr"><MapPin className="w-3.5 h-3.5" /> Address</TabsTrigger>
              <TabsTrigger value="comp" className="rounded-sm gap-1.5 text-xs" data-testid="tab-comp"><Scale className="w-3.5 h-3.5" /> Compliance</TabsTrigger>
              <TabsTrigger value="comm" className="rounded-sm gap-1.5 text-xs" data-testid="tab-comm"><Briefcase className="w-3.5 h-3.5" /> Commercial</TabsTrigger>
              <TabsTrigger value="attach" className="rounded-sm gap-1.5 text-xs" data-testid="tab-attach"><Paperclip className="w-3.5 h-3.5" /> Attachments</TabsTrigger>
            </TabsList>

            <TabsContent value="id" className="space-y-3 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{noun} code</Label><Input value={form.customer_code} onChange={(e) => setForm({ ...form, customer_code: e.target.value })} placeholder={type === "vendor" ? "V-001" : "C-001"} className="rounded-sm mt-1 font-mono" data-testid="party-code" /></div>
                <div><Label>{type === "vendor" ? "Expense classification" : "Customer type"}</Label>
                  <SearchSelect options={type === "vendor" ? VENDOR_TYPES : CUSTOMER_TYPES} value={form.customer_type} onChange={(v) => setForm({ ...form, customer_type: v })} testId="party-cust-type" />
                </div>
              </div>
              <div><Label>Legal name (per PAN / GST)</Label><Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} className="rounded-sm mt-1" data-testid="party-legal-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Trade name (doing-business-as)</Label><Input value={form.trade_name} onChange={(e) => setForm({ ...form, trade_name: e.target.value, name: e.target.value })} className="rounded-sm mt-1" data-testid="party-trade-name" /></div>
                <div><Label>Display name</Label><Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Shown on invoices" className="rounded-sm mt-1" data-testid="party-display-name" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" className="rounded-sm mt-1" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm mt-1" /></div>
              </div>
            </TabsContent>

            <TabsContent value="addr" className="space-y-3 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Address Line 1</Label><Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} className="rounded-sm mt-1" data-testid="party-addr1" /></div>
                <div><Label>Address Line 2</Label><Input value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} className="rounded-sm mt-1" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-sm mt-1" data-testid="party-city" /></div>
                <div><Label>PIN</Label><Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className="rounded-sm mt-1" /></div>
                <div><Label>State *</Label>
                  <SearchSelect options={STATE_OPTS} value={form.state_code}
                    onChange={(v) => { const st = STATES.find(([c]) => c === v); setForm({ ...form, state_code: v, state: st?.[1] || "" }); }}
                    testId="party-state" placeholder="Select state" />
                </div>
              </div>
              <div className="border-t pt-3 mt-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Ship-to (leave blank to use bill-to)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Shipping address</Label><Textarea value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} rows={2} className="rounded-sm mt-1" /></div>
                  <div><Label>Ship state code</Label>
                    <SearchSelect options={STATE_OPTS} value={form.shipping_state_code} onChange={(v) => setForm({ ...form, shipping_state_code: v })} testId="party-ship-state" placeholder="—" />
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="comp" className="space-y-3 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>GST Status</Label>
                  <Select value={form.gst_status} onValueChange={(v) => setForm({ ...form, gst_status: v })}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="registered">Registered</SelectItem><SelectItem value="unregistered">URD — Unregistered</SelectItem><SelectItem value="composition">Composition</SelectItem><SelectItem value="sez">SEZ / Export</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>GSTIN <span className="text-[10px] text-muted-foreground">(optional · leave blank if unregistered)</span></Label>
                  <div className="flex gap-1 mt-1">
                    <Input value={form.gstin} disabled={form.gst_status === "unregistered"} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} className="rounded-sm font-mono" placeholder="15-char GSTIN · optional" data-testid="party-gstin" />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={gstinFetching || form.gst_status === "unregistered" || !form.gstin || form.gstin.length !== 15}
                      onClick={async () => {
                        setGstinFetching(true);
                        try {
                          const r = await api.get(`/gstin/lookup`, { params: { gstin: form.gstin } });
                          if (!r.data.valid) { toast.error(r.data.error || "Invalid GSTIN"); return; }
                          setForm((f) => applyGstinLookup(f, r.data));
                          toast.success("Fetched from GST portal");
                        } catch (e) { toast.error(e?.response?.data?.detail || "Fetch failed"); }
                        finally { setGstinFetching(false); }
                      }}
                      className="rounded-sm whitespace-nowrap"
                      data-testid="party-gstin-fetch"
                    >{gstinFetching ? "Fetching…" : "⤓ Fetch"}</Button>
                  </div>
                </div>
              </div>
              <div><Label>PAN * <span className="text-red-500 text-xs">({type === "vendor" ? "mandatory for TDS compliance" : "mandatory for TCS 206C"})</span></Label>
                <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" placeholder="ABCDE1234F" data-testid="party-pan" />
              </div>
              {/* Iter-220 · TCS applies ONLY to sales (Section 206C 1H) —
                  hide the entire TCS block on the vendor form. */}
              {type === "customer" && (
              <div className="border-t pt-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">TCS setup — Sec 206C(1H) · sale of goods &gt; ₹50L</div>
                <div className="flex items-center gap-3 mb-2">
                  <Switch checked={form.tcs_applicable} onCheckedChange={(v) => setForm({ ...form, tcs_applicable: v })} data-testid="tcs-toggle" />
                  <Label>TCS applicable on invoices to this customer</Label>
                </div>
                {form.tcs_applicable && (
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Section</Label><Input value={form.tcs_section} onChange={(e) => setForm({ ...form, tcs_section: e.target.value })} className="rounded-sm mt-1 font-mono" /></div>
                    <div><Label>Rate %</Label><Input type="number" step="0.001" value={form.tcs_rate} onChange={(e) => setForm({ ...form, tcs_rate: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
                    <div><Label>Threshold ₹</Label><Input type="number" value={form.tcs_threshold_inr} onChange={(e) => setForm({ ...form, tcs_threshold_inr: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
                  </div>
                )}
              </div>
              )}
              <div className="border-t pt-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">TDS defaults ({type === "vendor" ? "auto-deduct on vendor payment" : "auto-deduct on customer receipt"})</div>
                <div className="flex items-center gap-3 mb-2">
                  <Switch checked={form.tds_deduct_default} onCheckedChange={(v) => setForm({ ...form, tds_deduct_default: v })} data-testid="tds-toggle" />
                  <Label>Auto-TDS ON by default</Label>
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <Switch checked={form.tds_visible_on_invoice} onCheckedChange={(v) => setForm({ ...form, tds_visible_on_invoice: v })} data-testid="tds-visible" />
                  <Label>Show TDS line on invoice PDF (else ledger-only)</Label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Section</Label><Input value={form.default_tds_section} onChange={(e) => setForm({ ...form, default_tds_section: e.target.value })} placeholder="194C · 194J · …" className="rounded-sm mt-1 font-mono" /></div>
                  <div><Label>Rate %</Label><Input type="number" step="0.01" value={form.default_tds_rate} onChange={(e) => setForm({ ...form, default_tds_rate: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
                </div>
              </div>
              <div className="border-t pt-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">MSMED Act 2020</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>MSME Status</Label>
                    <Select value={form.msme_status} onValueChange={(v) => setForm({ ...form, msme_status: v })}>
                      <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="not_registered">Not registered</SelectItem><SelectItem value="micro">Micro</SelectItem><SelectItem value="small">Small</SelectItem><SelectItem value="medium">Medium</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Udyam #</Label><Input value={form.udyam_number} onChange={(e) => setForm({ ...form, udyam_number: e.target.value })} className="rounded-sm mt-1 font-mono" /></div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="comm" className="space-y-3 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{type === "vendor" ? "Procurement owner" : "Sales person"}</Label><Input value={form.sales_person_name} onChange={(e) => setForm({ ...form, sales_person_name: e.target.value })} placeholder="Employee name" className="rounded-sm mt-1" data-testid="party-salesperson" /></div>
                <div><Label>{type === "vendor" ? "Payment terms (days)" : "Credit terms (days)"}</Label><Input type="number" value={form.credit_terms_days} onChange={(e) => setForm({ ...form, credit_terms_days: Number(e.target.value) })} className="rounded-sm mt-1" data-testid="party-credit-terms" /></div>
              </div>
              <div><Label>Opening balance</Label><Input type="number" value={form.opening_balance || 0} onChange={(e) => setForm({ ...form, opening_balance: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
              <div><Label>{type === "vendor" ? "Credit limit from vendor (₹)" : "Credit limit (₹)"}</Label><Input type="number" value={form.credit_limit || 0} onChange={(e) => setForm({ ...form, credit_limit: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
            </TabsContent>

            <TabsContent value="attach" className="space-y-3 mt-4">
              <div className="text-xs text-muted-foreground">Contract / agreement attachment can be uploaded after the {noun.toLowerCase()} is saved — from the party detail page (uses Emergent Object Storage).</div>
              <div className="border-2 border-dashed rounded-sm p-8 text-center text-muted-foreground text-sm">
                <Paperclip className="w-6 h-6 mx-auto mb-2 opacity-40" />
                Save the {noun.toLowerCase()} first, then upload the contract from the row menu.
              </div>
            </TabsContent>
          </Tabs>
          <Button onClick={create} className="w-full rounded-sm bg-accent text-white mt-4" data-testid="party-save">Save {noun}</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
